import { create } from 'zustand';
import { extractJwtTokenFromUrl, isKnownHostUrl, removeTokenFromUrl } from 'utils/urlUtils';
import { KNOWN_HOSTS } from 'shared/config/firebase';
import { persist, createJSONStorage } from 'zustand/middleware';

interface JwtAuthState {
  jwtToken: string | null;
  isJwtAuthenticated: boolean;
  isVerified: boolean;
  isInKnownHost: boolean;
  shouldUseJwt: boolean; // Flag to indicate whether JWT auth should be used instead of Firebase
  
  // Actions
  setJwtToken: (token: string | null) => void;
  setIsVerified: (isVerified: boolean) => void;
  initializeFromUrl: () => void;
  clearJwtAuth: () => void;
}

// Get initial token from localStorage if available
const getStoredToken = (): string | null => {
  try {
    const storedAuth = sessionStorage.getItem('jwt-auth');
    if (storedAuth) {
      const parsedAuth = JSON.parse(storedAuth);
      return parsedAuth.state.jwtToken;
    }
  } catch (error) {
    console.error('Error retrieving stored JWT token:', error);
  }
  return null;
};

const jwtAuthStore = create<JwtAuthState>()(
  persist(
    (set, get) => ({
      jwtToken: getStoredToken(),
      isJwtAuthenticated: !!getStoredToken(),
      isVerified: !!getStoredToken(), // Assume stored token is already verified
      isInKnownHost: isKnownHostUrl(KNOWN_HOSTS),
      shouldUseJwt: !!getStoredToken() && isKnownHostUrl(KNOWN_HOSTS),
      
      setJwtToken: (token: string | null) => {
        const isJwtAuthenticated = !!token;
        set({ 
          jwtToken: token,
          isJwtAuthenticated,
          // Only set shouldUseJwt true if we have a token and are in a known host
          shouldUseJwt: isJwtAuthenticated && get().isInKnownHost
        });
      },
      
      setIsVerified: (isVerified: boolean) => {
        set({ 
          isVerified,
          // Update shouldUseJwt based on verification status and known host
          shouldUseJwt: isVerified && get().isInKnownHost && !!get().jwtToken
        });
      },

      initializeFromUrl: () => {
        // Get token from URL
        const token = extractJwtTokenFromUrl();
        
        // Check if we're in a known host
        const isInKnownHost = isKnownHostUrl(KNOWN_HOSTS);
        
        // If we have a token in the URL, use it
        if (token) {
          set({ 
            jwtToken: token,
            isJwtAuthenticated: true,
            isInKnownHost,
            isVerified: false,
            // Initially, we'll set shouldUseJwt if we have a token and are in a known host
            // This will be updated after verification
            shouldUseJwt: isInKnownHost
          });
          removeTokenFromUrl();
        } 
        // Otherwise, keep using the persisted token if available
        else if (!get().jwtToken) {
          // Only update isInKnownHost if no token already set
          set({
            isInKnownHost
          });
        }
      },
      
      clearJwtAuth: () => {
        set({
          jwtToken: null,
          isJwtAuthenticated: false,
          isVerified: false,
          shouldUseJwt: false
        });
      }
    }),
    {
      name: 'jwt-auth', 
      storage: createJSONStorage(() => sessionStorage),
      // Only persist these keys
      partialize: (state) => ({
        jwtToken: state.jwtToken,
        isJwtAuthenticated: state.isJwtAuthenticated,
        isVerified: state.isVerified,
        shouldUseJwt: state.shouldUseJwt
      }),
    }
  )
);

export default jwtAuthStore; 